import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { previewTemplate, DripEmailTemplate } from '@/actions/drip-campaigns';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DripEmailTemplate;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const [sampleData, setSampleData] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<{
    subject: string;
    html: string;
    plainText: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'html' | 'plaintext'>('html');

  useEffect(() => {
    if (isOpen && template) {
      // Initialize sample data with default values
      const initialData: Record<string, string> = {};
      template.variables.forEach((variable) => {
        initialData[variable.name] = variable.default || '';
      });
      setSampleData(initialData);
      loadPreview(initialData);
    }
  }, [isOpen, template]);

  const loadPreview = async (data: Record<string, string>) => {
    try {
      setLoading(true);
      const response = await previewTemplate(template.id, data);
      if (response.success && response.data) {
        setPreview({
          subject: response.data.subject,
          html: response.data.html,
          plainText: response.data.plainText,
        });
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load template preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSampleDataChange = (variableName: string, value: string) => {
    setSampleData((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  };

  const handleRefreshPreview = () => {
    loadPreview(sampleData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Template Preview
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {template.template_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar - Sample Data */}
          <div className="w-80 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Sample Data
              </h3>
              <button
                onClick={handleRefreshPreview}
                disabled={loading}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                title="Refresh Preview"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {template.variables.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This template has no variables
              </p>
            ) : (
              <div className="space-y-4">
                {template.variables.map((variable) => (
                  <div key={variable.name}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {variable.label}
                      {variable.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={sampleData[variable.name] || ''}
                      onChange={(e) =>
                        handleSampleDataChange(variable.name, e.target.value)
                      }
                      placeholder={variable.default}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {`{{${variable.name}}}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main - Preview */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading preview...</p>
                </div>
              </div>
            ) : preview ? (
              <>
                {/* Subject Line */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subject:</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {preview.subject}
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
                  <button
                    onClick={() => setActiveTab('html')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'html'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    HTML Preview
                  </button>
                  <button
                    onClick={() => setActiveTab('plaintext')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'plaintext'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Plain Text
                  </button>
                </div>

                {/* Preview Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'html' ? (
                    <div className="bg-white rounded-lg shadow-sm">
                      <iframe
                        srcDoc={preview.html}
                        className="w-full min-h-[600px] border border-gray-200 rounded-lg"
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg">
                      <pre className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white font-mono">
                        {preview.plainText}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400">No preview available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Category:</span>{' '}
            <span className="capitalize">{template.category.replace('_', ' ')}</span>
            {template.is_system_template && (
              <span className="ml-3 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded">
                System Template
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
